import { useMemo, useState } from "react";
import { fmtDate, fmtDayShort } from "@/lib/format";
import { GitCompare, ChevronRight } from "lucide-react";

/** Day-by-day diff between the current plan and the simulated plan:
 *  a calendar strip of changed days, plus a before/after breakdown with the driver for each change. */
export default function SimDiff({ sim, recovery = [], taper = 0, target }) {
  const [openDay, setOpenDay] = useState(null);
  const hasMod = !!sim?.modified;

  const { rows, changedSet } = useMemo(() => {
    if (!hasMod) return { rows: [], changedSet: new Set() };
    const curMap = Object.fromEntries((sim.current.trajectory || []).map((p) => [p.date, p]));
    const taperStart = taper > 0 && target ? new Date(new Date(target).getTime() - taper * 864e5) : null;
    const rows = (sim.modified.trajectory || []).map((m) => {
      const c = curMap[m.date];
      if (!c) return null;
      const dLoad = +(((m.load ?? 0) - (c.load ?? 0))).toFixed(2);
      const dRead = +((m.readiness - c.readiness)).toFixed(1);
      const dRest = +((m.rest - c.rest)).toFixed(1);
      const dSharp = +((m.sharpness - c.sharpness)).toFixed(1);
      const dRisk = +(((m.injury_risk ?? 0) - (c.injury_risk ?? 0))).toFixed(1);
      const changed = Math.abs(dRead) >= 0.5 || Math.abs(dRest) >= 0.5 || Math.abs(dLoad) >= 0.05 || Math.abs(dRisk) >= 0.5;
      let driver = null;
      if (recovery.includes(m.date)) driver = "You converted this day to recovery, so prescribed work was removed.";
      else if (taperStart && new Date(m.date) >= taperStart) driver = `Inside the ${taper}-day taper before ${fmtDate(target)}, so load was reduced.`;
      else if (dLoad < -0.01) driver = "Knock-on effect: less accumulated load carried into this day.";
      else if (dLoad > 0.01) driver = "Knock-on effect: more accumulated load carried into this day.";
      else if (changed) driver = null;
      return { date: m.date, changed, driver, m, c, dLoad, dRead, dRest, dSharp, dRisk };
    }).filter(Boolean);
    return { rows, changedSet: new Set(rows.filter((r) => r.changed).map((r) => r.date)) };
  }, [sim, recovery, taper, target, hasMod]);

  if (!hasMod) return null;
  const changed = rows.filter((r) => r.changed);
  const drivers = {};
  changed.forEach((r) => { const k = r.driver || "Unattributed"; drivers[k] = (drivers[k] || 0) + 1; });

  return (
    <div className="border border-border mt-6" data-testid="sim-diff-panel">
      <div className="px-4 py-3 border-b border-border font-heading font-bold tracking-tight flex flex-wrap items-center gap-2">
        <GitCompare className="w-4 h-4" /> What changed · {changed.length} day(s)
        <span className="text-xs text-muted-foreground font-normal ml-auto">{recovery.length} recovery · {taper}d taper</span>
      </div>

      {/* calendar strip: every day in the horizon, changed days marked */}
      <div className="p-3 border-b border-border scroll-x" data-testid="sim-diff-strip">
        <div className="flex gap-1 min-w-max">
          {rows.map((r) => (
            <button key={r.date} data-testid={`diff-strip-${r.date}`} onClick={() => setOpenDay(r.changed ? r.date : null)}
              title={`${fmtDate(r.date)}${r.changed ? " · changed" : " · unchanged"}`}
              className={`w-6 h-9 border text-[9px] leading-tight flex flex-col items-center justify-center transition-colors ${
                r.changed ? "border-foreground bg-foreground text-background" : "border-border text-muted-foreground"}
                ${openDay === r.date ? "ring-2 ring-brand-magenta" : ""}`}>
              <span>{new Date(r.date).getDate()}</span>
            </button>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-2">Filled squares changed. Empty squares are unchanged days · the plan there is untouched.</p>
      </div>

      {/* driver summary for the whole scenario */}
      <div className="px-4 py-3 border-b border-border flex flex-wrap gap-2" data-testid="sim-diff-drivers">
        {Object.entries(drivers).map(([k, n]) => (
          <span key={k} className="text-[11px] border border-border px-2 py-1 text-muted-foreground">
            <b className="text-foreground tabular-nums">{n}</b> {k === "Unattributed" ? "day(s) the model could not attribute" : `day(s): ${k}`}
          </span>
        ))}
        {changed.length === 0 && <span className="text-sm text-muted-foreground">No day-level changes.</span>}
      </div>

      <div className="max-h-80 overflow-y-auto divide-y divide-border" data-testid="sim-diff-list">
        {changed.map((r) => (
          <div key={r.date} data-testid={`sim-diff-${r.date}`}>
            <button onClick={() => setOpenDay(openDay === r.date ? null : r.date)}
              className="w-full text-left p-3 flex flex-wrap items-center gap-3 text-sm hover:bg-surface transition-colors">
              <ChevronRight className={`w-3.5 h-3.5 transition-transform ${openDay === r.date ? "rotate-90" : ""}`} />
              <span className="text-xs text-muted-foreground w-24 tabular-nums">{fmtDayShort(r.date)}</span>
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 border border-border text-muted-foreground">
                {recovery.includes(r.date) ? "→ recovery day" : "load changed"}
              </span>
              <span className="ml-auto flex gap-3 tabular-nums text-xs">
                <span className={r.dRead >= 0 ? "text-traffic-green" : "text-traffic-red"}>RED {r.dRead > 0 ? "+" : ""}{r.dRead}</span>
                <span className={r.dRest >= 0 ? "text-traffic-green" : "text-traffic-red"}>RST {r.dRest > 0 ? "+" : ""}{r.dRest}</span>
                <span className={r.dRisk <= 0 ? "text-traffic-green" : "text-traffic-red"}>RISK {r.dRisk > 0 ? "+" : ""}{r.dRisk}</span>
              </span>
            </button>
            {openDay === r.date && (
              <div className="px-4 pb-4 grid sm:grid-cols-2 gap-4" data-testid={`sim-diff-detail-${r.date}`}>
                <div className="border border-border">
                  <div className="px-3 py-1.5 border-b border-border text-[10px] uppercase tracking-widest text-muted-foreground">Before → after</div>
                  <table className="w-full text-xs">
                    <tbody>
                      {[["Load", r.c.load ?? 0, r.m.load ?? 0], ["Readiness", r.c.readiness, r.m.readiness],
                        ["Rest+", r.c.rest, r.m.rest], ["Sharpness", r.c.sharpness, r.m.sharpness],
                        ["Effort+", r.c.effort, r.m.effort], ["Injury risk", r.c.injury_risk, r.m.injury_risk]].map(([k, a, b]) => (
                        <tr key={k} className="border-b border-border last:border-0">
                          <td className="px-3 py-1.5 text-muted-foreground">{k}</td>
                          <td className="px-3 py-1.5 tabular-nums text-right">{a ?? "-"}</td>
                          <td className="px-2 py-1.5 text-muted-foreground">→</td>
                          <td className="px-3 py-1.5 tabular-nums font-bold text-right">{b ?? "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="border border-border p-3 text-xs">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1.5">Why this day changed</div>
                  {r.driver
                    ? <p className="leading-relaxed">{r.driver}</p>
                    : <p className="leading-relaxed text-traffic-amber">The model cannot attribute this change to a single driver · it is a downstream effect of other changes in the scenario. No reason is being invented.</p>}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
